import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { findSvfEntry } from "@/data/svfEntries";
import { MATERIAL_EVIDENCE } from "@/data/ancientImages";
import { VITA_INTRO, VITA_SECTIONS, VITA_LABELS, VITA_DOCTRINE_HEADING } from "@/data/vitaZeno";
import { AncientImageFigure } from "@/pages/ancient/AncientImageFigure";
import { useAlLabels } from "@/pages/ancient/common";
import { pickLang } from "@/data/stoicosCards";
import { useSEO } from "@/lib/useSEO";

const svfSlug = (ref) => {
  const m = ref.match(/^SVF I\.(\d+)$/);
  return m ? `svf-1-${m[1]}` : null;
};

/* Anchor chip: links to the individual SVF record once it is published. */
const SvfAnchor = ({ a }) => {
  const slug = svfSlug(a.svf);
  const published = slug && findSvfEntry(slug);
  const cls = "inline-flex items-baseline gap-1.5 border border-white/10 px-2.5 py-1 text-[11px] tracking-[0.08em] text-fog/75";
  const inner = (<><span className="text-gold/90">{a.svf}</span><span className="text-fog/50">·</span><span>{a.source}</span></>);
  return published
    ? <Link to={`/ancient-library/svf/zeno/${slug}`} className={cls + " hover:border-gold/50 transition-colors"} data-testid={`vita-anchor-${slug}`}>{inner}</Link>
    : <span className={cls} data-testid={`vita-anchor-${a.svf.replace(/[^\w-]+/g, "-")}`}>{inner}</span>;
};

export default function ZenoVitaPage() {
  const { lang, labels } = useAlLabels();
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ block: "start" });
  }, [hash]);
  useSEO({
    title: "The Life of Zeno — Vita from SVF I.1–46 | StoicOs Ancient Library",
    description: "The life of Zeno of Citium reconstructed from the ancient testimonia of SVF I.1–46 (Diogenes Laërtius and others): origins, teachers, the Painted Stoa, character, honours, death and legacy — with source anchors and reliability notes.",
    canonical: "https://stoicos.ai/ancient-library/svf/zeno/vita",
  });
  const imgById = Object.fromEntries(MATERIAL_EVIDENCE.map((m) => [m.id, m]));
  return (
    <main className="min-h-screen bg-ink text-bone" data-testid="zeno-vita-page">
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">
        <nav className="text-[11px] tracking-[0.22em] uppercase text-fog/60" data-testid="vita-breadcrumb">
          <Link to="/ancient-library" className="hover:text-gold transition-colors">Ancient Library</Link>
          <span className="mx-2">/</span>
          <Link to="/ancient-library/svf" className="hover:text-gold transition-colors">SVF</Link>
          <span className="mx-2">/</span>
          <Link to="/ancient-library/svf/zeno" className="hover:text-gold transition-colors" data-testid="vita-back-to-zeno">Zeno of Citium</Link>
          <span className="mx-2">/</span>
          <span className="text-gold">Vita</span>
        </nav>

        <header className="mt-10">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold">SVF I.1–46</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-bone" data-testid="vita-title">{pickLang(VITA_INTRO.title, lang)}</h1>
          <p className="mt-6 text-fog/80 font-reading text-base md:text-lg leading-[1.85]" data-testid="vita-intro">{pickLang(VITA_INTRO.body, lang)}</p>
        </header>

        {VITA_SECTIONS.map((s) => (
          <section key={s.id} id={`vita-${s.id}`} className="mt-16 scroll-mt-28" data-testid={`vita-section-${s.id}`}>
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold">{pickLang(s.title, lang)}</h2>
            {s.labels && s.labels.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {s.labels.map((lb) => (
                  <span key={lb} className="text-[10px] tracking-[0.2em] uppercase text-fog/60 border border-white/10 px-2 py-0.5" data-testid={`vita-label-${s.id}-${lb}`}>
                    {pickLang(VITA_LABELS[lb], lang)}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-5 text-bone/85 font-reading text-base md:text-lg leading-[1.9]">{pickLang(s.body, lang)}</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid={`vita-anchors-${s.id}`}>
              {s.anchors.map((a, i) => <SvfAnchor key={i} a={a} />)}
            </div>
            {s.doctrine && s.doctrine.length > 0 && (
              <div className="mt-4" data-testid={`vita-doctrine-${s.id}`}>
                <p className="text-[10px] tracking-[0.25em] uppercase text-gold/70">{pickLang(VITA_DOCTRINE_HEADING, lang)}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.doctrine.map((d) => (
                    <Link
                      key={d.slug}
                      to={`/ancient-library/svf/zeno/${d.slug}`}
                      className="inline-flex items-baseline gap-1.5 border border-gold/25 px-2.5 py-1 text-[11px] tracking-[0.08em] text-fog/80 hover:border-gold/60 transition-colors"
                      data-testid={`vita-doctrine-link-${d.slug}`}
                    >
                      <span className="text-gold/90">{d.ref}</span>
                      <span className="text-fog/50">·</span>
                      <span>{pickLang(d.title, lang)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {s.image && imgById[s.image] && (
              <div className="mt-8">
                <AncientImageFigure img={imgById[s.image]} lang={lang} labels={labels} portraitNarrow={!!imgById[s.image].portrait} />
              </div>
            )}
          </section>
        ))}

        <div className="mt-20 pt-8 border-t border-white/10">
          <Link to="/ancient-library/svf/zeno" className="text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold/80 transition-colors" data-testid="vita-footer-back">
            ← Zeno of Citium — {labels.entries}
          </Link>
        </div>
      </div>
    </main>
  );
}
